import { useEffect, useState, useRef } from "react";
import { Joyride, EVENTS, STATUS } from "react-joyride";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const RAW_STEPS = [
  {
    nav: null,
    title: "Welcome to your Ludus, Lanista",
    content:
      "Salve! You command a gladiator training school. Let's take a quick tour of how to rise to glory in the arena. You can skip anytime.",
  },
  {
    nav: null,
    title: "Recruit fighters",
    content:
      "Enlist your gladiators from the Recruit Pool — open it with the glowing \"Recruit Pool\" button on your Ludus dashboard or Active Roster page. The pool refreshes every 12 hours with fresh talent, each with a hidden potential you can't see yet.",
  },
  {
    nav: "training",
    title: "Train them — and scout their potential",
    content:
      "Assign gladiators to training. As they train over time, their hidden potential is gradually revealed (scouting) — and higher potential means faster stat growth. Watch their skills and potential rating sharpen week by week.",
  },
  {
    nav: "roster",
    title: "Keep only the best",
    content:
      "Once training reveals a fighter's potential, cut your losses on the weak ones. If a gladiator turns out POOR or AVERAGE, release them from your Active Roster — you only want elite talent representing your ludus!",
  },
  {
    nav: "equipment",
    title: "Forge & equip gear",
    content:
      "Win battles to salvage fragments, then craft and equip class-specific weapons and armor at the Blacksmith.",
  },
  {
    nav: "challenges",
    title: "Fight for glory",
    content:
      "Challenge other lanistae to tactical auto-sim battles, climb the rankings, and earn arena salvage.",
  },
  {
    nav: "leaderboard",
    title: "Climb the rankings",
    content:
      "Track your standing against rival schools. Win ranked fights to rise to the top of the arena.",
  },
  {
    nav: null,
    title: "You're ready, Lanista!",
    content:
      "That's the basics. Recruit, train, scout, cull the weak, equip, and conquer the arena. Glory awaits — to battle!",
  },
];

function buildSteps(isMobile) {
  return RAW_STEPS.map((s) => {
    // On mobile the sidebar nav is hidden behind the menu, so anchored steps can't
    // find their targets and the tour would auto-skip. Use centered modals instead.
    if (isMobile || !s.nav) {
      return { target: "body", placement: "center", title: s.title, content: s.content };
    }
    return { target: `[data-testid="nav-${s.nav}"]`, placement: "right", title: s.title, content: s.content };
  });
}

const OPTIONS = {
  arrowColor: "#18181b",
  backgroundColor: "#18181b",
  overlayColor: "rgba(0,0,0,0.78)",
  primaryColor: "#92400e",
  textColor: "#e4e4e7",
  zIndex: 10000,
  showProgress: true,
  closeButtonAction: "skip",
  buttons: ["back", "primary", "skip"],
};

const STYLES = {
  tooltip: { border: "1px solid #78350f", borderRadius: 6, fontFamily: "Manrope, sans-serif" },
  tooltipTitle: {
    color: "#fcd34d",
    fontFamily: "Cinzel, serif",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontSize: 16,
  },
  buttonPrimary: {
    backgroundColor: "#92400e",
    border: "1px solid #b45309",
    color: "#fcd34d",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontSize: 12,
  },
  buttonBack: { color: "#a1a1aa", fontSize: 12 },
  buttonSkip: { color: "#71717a", fontSize: 12 },
};

export default function Tutorial() {
  const { user } = useAuth();
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const autoStartedRef = useRef(false);
  const timerRef = useRef(null);

  const startTour = () => {
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    setSteps(buildSteps(isMobile));
    setRun(true);
  };

  useEffect(() => {
    // Tutorial disabled - no auto-start for new users
    // Users will not see the tutorial automatically
  }, [user]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  useEffect(() => {
    // Replay tutorial listener disabled
  }, []);

  const handleCallback = (data) => {
    const finished = data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED;
    if (finished || data.type === EVENTS.TOUR_END) {
      setRun(false);
    }
  };

  if (!run || !steps.length) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      disableScrolling
      scrollToFirstStep={false}
      callback={handleCallback}
      styles={{ ...STYLES, options: OPTIONS }}
      locale={{ back: "Back", close: "Close", last: "To Battle", next: "Next", skip: "Skip tour" }}
    />
  );
}
